import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  CUSTOM_BUTTON_COLOR,
  CUSTOM_BUTTON_SIZE,
  CUSTOM_BUTTON_VARIANT,
  CustomButton,
} from 'src/components/CustomButton';

import { useStyles } from './useStyles';

export const DEFAULT_FLEX_GAP = 8;

const propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  flexGap: PropTypes.number,
};

const defaultProps = {
  className: null,
  flexGap: DEFAULT_FLEX_GAP,
};

export const OverflowItems = ({ className, children, flexGap = DEFAULT_FLEX_GAP }) => {
  const styles = useStyles({ flexGap });

  const containerRef = useRef(null);
  const childRefs = useRef([]);
  const moreRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleIndices, setVisibleIndices] = useState([]);
  const [lastVisibleRight, setLastVisibleRight] = useState(0);

  useEffect(() => {
    childRefs.current = childRefs.current.slice(0, React.Children.count(children));
  }, [children]);

  useEffect(() => {
    const updateLayout = () => {
      if (isExpanded || !containerRef.current) {
        return;
      }

      const containerWidth = containerRef.current.offsetWidth;
      const moreWidth = moreRef.current?.offsetWidth || 0;
      let total = 0;
      const visible = [];

      for (let i = 0; i < children.length; i++) {
        const el = childRefs.current[i];
        if (el) {
          const currentWidth = (i > 0 ? flexGap : 0) + el.offsetWidth;
          const currentRight = total + currentWidth;

          if (currentRight > containerWidth) {
            break;
          }

          total = currentRight;
          visible.push(i);
        }
      }

      // If couldn't fit all children, add the "More" button width
      if (visible.length < children.length) {
        while (total + flexGap + moreWidth > containerWidth) {
          const index = visible.pop();
          const el = childRefs.current[index];
          if (el) {
            total -= el.offsetWidth + flexGap;
          }
        }
      }

      setLastVisibleRight(total);
      setVisibleIndices(visible);
    };

    const observer = new ResizeObserver(updateLayout);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    updateLayout();

    return () => observer.disconnect();
  }, [children, isExpanded, flexGap]);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const isCollapsed = !isExpanded;
  const shouldShowToggle = visibleIndices.length < children.length;

  return (
    <div
      className={cx(styles.root, className, {
        [styles.expanded]: isExpanded,
      })}
      ref={containerRef}
    >
      {React.Children.map(children, (child, index) => {
        const isVisible = isExpanded || visibleIndices.includes(index);
        return (
          <span
            className={cx(styles.childWrapper, { [styles.visible]: isVisible })}
            ref={(el) => (childRefs.current[index] = el)}
          >
            {child}
          </span>
        );
      })}

      {shouldShowToggle && isCollapsed && (
        <CustomButton
          className={cx(styles.button, styles.buttonMore)}
          style={{ left: lastVisibleRight + flexGap }}
          ref={moreRef}
          variant={CUSTOM_BUTTON_VARIANT.text}
          size={CUSTOM_BUTTON_SIZE.smaller}
          color={CUSTOM_BUTTON_COLOR.default}
          onClick={toggleExpanded}
        >
          More...
        </CustomButton>
      )}

      {shouldShowToggle && isExpanded && (
        <CustomButton
          className={cx(styles.button, styles.buttonLess)}
          variant={CUSTOM_BUTTON_VARIANT.text}
          size={CUSTOM_BUTTON_SIZE.smaller}
          color={CUSTOM_BUTTON_COLOR.default}
          onClick={toggleExpanded}
        >
          Less
        </CustomButton>
      )}
    </div>
  );
};

OverflowItems.displayName = 'OverflowItems';
OverflowItems.propTypes = propTypes;
OverflowItems.defaultProps = defaultProps;
